import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import { apiRequest, teamPhoto } from '../../utils';
import './CompanyEventRankingScreen.css';

/* The board for the wall.

   ★★ This shows WHAT THE FIELD SEES, not what the organiser knows: the payload behind it is
   `fetch_ranking` with the `Event.leaderboardquarter` cap left on — the same read as the players'
   /leaderboard. At a Hercules the board opens round by round and the ceremony is the product, so a
   projector that already shows the final standing gives the prize-giving away before the host can.
   The switch on the bar lifts the cap for the last round, and the header then says so out loud.

   ★★ It PAGES rather than scrolls. A list that creeps upward is unreadable from the back of a room
   and it always moves just as somebody looks up; ten rows that hold still for nine seconds can
   actually be read. The page size is measured off a real row against the real screen, so the same
   screen fills a laptop, a 16:9 beamer and a portrait lobby display without a breakpoint.

   ★ It refreshes itself every 25 seconds. A host entering a heat result on their laptop is not
   going to walk over to the projector afterwards. */

const REFRESH_MS = 25000;
const PAGE_MS = 9000;

const formatPoints = (n) => Math.round(Number(n) || 0).toLocaleString();

const teamImage = (team) => ({
  photo: teamPhoto(team.photo_url, team.company_picture),
  contain: !team.photo_url && !!team.company_picture,
});

const Row = ({ team, rank, innerRef }) => {
  const { t } = useTranslation();
  const { photo, contain } = teamImage(team);
  const medal = rank <= 3 ? ` rs-row--m${rank}` : '';

  return (
    <div className={`rs-row${medal}`} ref={innerRef}>
      <span className="rs-row__rank">{rank}</span>
      <span
        className={`rs-row__avatar ${contain ? 'rs-row__avatar--contain' : ''}`}
        style={{ backgroundImage: `url(${photo})` }}
      />
      <span className="rs-row__body">
        <span className="rs-row__name">{team.name}</span>
        {team.company_name && <span className="rs-row__company">{team.company_name}</span>}
      </span>
      {team.house_color && (
        <span className="rs-row__house" style={{ background: team.house_color }} />
      )}
      <span className="rs-row__points">
        {formatPoints(team.points)}
        <span className="rs-row__pts">{t('pts')}</span>
      </span>
    </div>
  );
};

const CompanyEventRankingScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [revealAll, setRevealAll] = useState(false);
  const [paused, setPaused] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(8);
  const [isFull, setIsFull] = useState(false);

  const listRef = useRef(null);
  const rowRef = useRef(null);

  const load = useCallback(() => {
    const query = revealAll ? '?reveal=all' : '';
    apiRequest('GET', `/company/event/${eventId}/ranking/screen${query}`)
      .then((res) => { setData(res); setFailed(false); })
      .catch(() => setFailed(true));
  }, [eventId, revealAll]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const teams = data?.teams || [];

  /* How many rows this screen holds — measured, not guessed. One real row against the real list
     box, re-measured when the window changes or we go fullscreen. */
  useEffect(() => {
    const measure = () => {
      const box = listRef.current;
      const row = rowRef.current;
      if (!box || !row) return;
      const rowHeight = row.getBoundingClientRect().height;
      if (!rowHeight) return;
      const fits = Math.max(1, Math.floor((box.clientHeight + 1) / rowHeight));
      setPerPage((prev) => (prev === fits ? prev : fits));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [teams.length, isFull]);

  /* Balanced pages: fourteen teams on a board that holds ten read as 7 + 7, not 10 + 4. A page
     with three rows on a wall looks like the list broke. */
  const { pages, size } = useMemo(() => {
    if (!teams.length) return { pages: 1, size: perPage };
    const count = Math.max(1, Math.ceil(teams.length / perPage));
    return { pages: count, size: Math.ceil(teams.length / count) };
  }, [teams.length, perPage]);

  const current = Math.min(page, pages - 1);
  const shown = teams.slice(current * size, current * size + size);

  useEffect(() => {
    if (paused || pages <= 1) return undefined;
    const id = setInterval(() => setPage((p) => (p + 1) % pages), PAGE_MS);
    return () => clearInterval(id);
  }, [paused, pages]);

  const toggleFull = useCallback(() => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  /* The laptop driving the beamer is usually out of reach of the mouse: space holds the page,
     the arrows step through it, f goes fullscreen. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ') { e.preventDefault(); setPaused((p) => !p); }
      else if (e.key === 'ArrowRight' || e.key === 'PageDown') setPage((p) => (p + 1) % pages);
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') setPage((p) => (p - 1 + pages) % pages);
      else if (e.key === 'f' || e.key === 'F') toggleFull();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pages, toggleFull]);

  const quarter = data?.leaderboard_quarter;
  const state = (() => {
    if (!data) return null;
    if (!data.capped) return t('rankingScreenFull');
    if (quarter == null || quarter === 0) return t('rankingScreenSealed');
    return t('rankingScreenUpTo', { n: quarter });
  })();

  return (
    <div className="rs">
      <div className="rs__head">
        <span className="rs__title">{data?.event?.name || t('leaderboard')}</span>
        {state && (
          <span className={`rs__state ${data.capped ? '' : 'rs__state--full'}`}>{state}</span>
        )}
      </div>

      <div className="rs__list" ref={listRef}>
        {failed && <p className="rs__note">{t('somethingWentWrong')}</p>}
        {!failed && !data && <p className="rs__note">{t('pleaseWait')}</p>}
        {!failed && data && teams.length === 0 && <p className="rs__note">{t('rankingEmpty')}</p>}
        {shown.map((team, i) => (
          <Row
            key={team.team_id || `${current}-${i}`}
            team={team}
            rank={current * size + i + 1}
            innerRef={i === 0 ? rowRef : null}
          />
        ))}
      </div>

      <div className="rs__foot">
        {pages > 1 && (
          <>
            <span className="rs__dots">
              {Array.from({ length: pages }, (unused, i) => (
                <span key={i} className={`rs__dot ${i === current ? 'is-on' : ''}`} />
              ))}
            </span>
            <span className="rs__pagenum">{`${current + 1} / ${pages}`}</span>
          </>
        )}
      </div>

      <div className="rs__bar">
        <button type="button" className="rs__btn" onClick={toggleFull}
                title={t('rankingScreenFullscreen')}>
          <Icon name="expand" theme="light" />
        </button>
        <button type="button" className="rs__btn" onClick={() => setPaused((p) => !p)}
                title={paused ? t('rankingScreenPlay') : t('rankingScreenPause')}>
          <Icon name={paused ? 'play' : 'pause'} theme="light" />
        </button>
        <button type="button" className={`rs__btn ${revealAll ? 'is-on' : ''}`}
                onClick={() => { setRevealAll((v) => !v); setPage(0); }}
                title={revealAll ? t('rankingScreenShowReleased') : t('rankingScreenShowAll')}>
          <Icon name="eye" theme="light" />
        </button>
        <button type="button" className="rs__btn"
                onClick={() => navigate(`/company-event/${eventId}/ranking`)}
                title={t('back')}>
          <Icon name="cross" theme="light" />
        </button>
      </div>
    </div>
  );
};

export default CompanyEventRankingScreen;
